import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, ArrowRight, Sparkles } from "lucide-react";
import api, { apiErrorMsg } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import {
  FloatingLabelInput, GradientButton, AuthSplitLeft,
  AuthHeader, AuthFooterNote, TrustBadgeStrip,
} from "../components/AuthUI";
import usePageTitle from "../lib/usePageTitle";

/**
 * CompleteProfile — mandatory second-step after Google sign-in.
 *
 * Google OAuth does not return a phone number, but HouseEntry requires it
 * (10 digits min) for callback / OTP / lead-recovery. This page captures it
 * and clears the backend `needs_profile_completion` flag.
 */
export default function CompleteProfile() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  usePageTitle("Complete Your Profile | HouseEntry");

  const dashboardFor = (u) =>
    u?.role === "admin" ? "/admin" :
    u?.role === "dealer" ? "/dealer" :
    u?.role === "builder" ? "/builder" :
    (u?.role === "team" || u?.role === "team_member") ? "/team" :
    "/";

  const submit = async (e) => {
    e.preventDefault();
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Required · 10 digits minimum");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/auth/complete-profile", { phone });
      await refreshUser();
      toast.success("Profile saved");
      nav(dashboardFor(r.data.user), { replace: true });
    } catch (err) {
      toast.error(apiErrorMsg(err, "Could not save profile"));
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] min-h-screen bg-zinc-50" data-testid="complete-profile-page">
      <AuthSplitLeft
        eyebrow="One last step"
        heading="Add your phone to unlock HouseEntry."
        sub="We use it for callbacks, OTP-based account recovery, and to route the right leads to you."
      />

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 relative">
        <div className="auth-blobs lg:hidden opacity-30" />
        <div className="w-full max-w-md relative">
          <AuthHeader showBack={false} />

          <div className="auth-glass rounded-2xl p-7 sm:p-8" data-testid="complete-profile-form">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#0055FF] mb-3 inline-flex items-center gap-1.5">
              <Sparkles size={11} /> Complete Profile
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-zinc-900">
              Hi {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-[14px] text-zinc-600 mt-1.5 mb-6">
              Add your mobile number so we can call you back for property matches.
            </p>

            <form onSubmit={submit} className="space-y-3.5">
              <FloatingLabelInput
                id="cp-phone"
                type="tel"
                label="Phone number"
                icon={Phone}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, "").slice(0, 13))}
                inputMode="tel"
                required
                error={error}
                hint="10 digits — used for callbacks & account recovery"
                testid="cp-phone-input"
              />

              <div className="pt-2">
                <GradientButton type="submit" loading={busy} testid="cp-submit" icon={ArrowRight}>
                  Save & Continue
                </GradientButton>
              </div>
            </form>

            <div className="mt-5 pt-5 border-t border-zinc-200 text-center text-[12.5px] text-zinc-500">
              Signed in as <span className="font-semibold text-zinc-700">{user?.email}</span>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <TrustBadgeStrip items={["Verified", "Encrypted", "Trusted"]} />
            <AuthFooterNote />
          </div>
        </div>
      </div>
    </div>
  );
}
